/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { UnitType } from 'src/api';
import { ScientificNotationPipe } from 'src/app/shared/pipes/scientific-notation/scientific-notation.pipe';
import { ConceptUnitService } from 'src/app/shared/services/unit.service';
import { CommonModule } from '@angular/common';
import { InputType } from '@ansys-internal/awc-angular/forms';
import { Icons } from '@ansys-internal/awc-angular/icons';
import { FormNumberInputComponent } from '@ansys-internal/awc-angular/form-number-input';
import { FormInputComponent } from '@ansys-internal/awc-angular/form-input';
import { AWCFormElementSize } from '@ansys-internal/awc-angular/form';
import { TooltipDirective } from '@ansys-internal/awc-angular/tooltip';
import { IconComponent } from "@ansys-internal/awc-angular/icon";

@Component({
    selector: 'app-input',
    templateUrl: './concept-input.component.html',
    styleUrls: ['./concept-input.component.scss'],
    imports: [CommonModule, FormInputComponent, TooltipDirective, IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ScientificNotationPipe]
})
export class ConceptInputComponent {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() value: any;
  @Input() type: InputType | 'RATIO' = InputType.TEXT;
  @Input() unit?: UnitType | string;
  @Input() id?: string;
  @Input() disabled = false;
  @Input() loading: boolean = false;
  @Input() disableRounding = false; // TODO: Check this
  @Input() highlight!: 'red' | 'green' | undefined; // TODO: Check this
  // legacy compatibility
  @Input() updater = false;
  @Input() readOnly = false;
  @Input() altLabel!: string;
  @Input() altValue!: string | number | undefined | null;
  @Input() tooltip!: string | undefined;
  @Output() valueChange = new EventEmitter<any>(); // new standard output
  @Output() update = new EventEmitter<any>(); // legacy alias for old <app-input> usage
  @Output() setLabelWidth: EventEmitter<number> = new EventEmitter<number>();

  protected readonly UnitType = UnitType;
  private finalValue!: any; // Track edited value as user types

  get suffixText(): string {
    if (!this.unit) return '';
    if (Object.values(UnitType).includes(this.unit as UnitType)) {
      return this.unitService.getChoice(this.unit as UnitType);
    }
    return String(this.unit);
  }
  protected isEditing = false;
  protected isDirty = false;
  protected ratioValue!: number; // For Ratio
  protected spinner: Icons = Icons.SPINNER;
  protected unitStr!: string;
  private initialValue: string | number = ''; // For Ratio
  protected valueCache: string | number | undefined | any;
  protected readonly InputType = InputType;
  protected readonly AWCFormElementSize = AWCFormElementSize;
  protected isValueTruncated: boolean = false; /** Set to true when the value is truncated or rounded by snPipe, so a tooltip can be shown */
  @ViewChild('formInputRef', { read: ElementRef }) private formInputElRef?: ElementRef;

  constructor(
    public unitService: ConceptUnitService,
    private snPipe: ScientificNotationPipe,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.transformUnitValue();

    if (this.type === 'RATIO') {
      this.setRatioValue(Number(this.value));
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateTextTruncation());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange) {
      setTimeout(() => this.updateTextTruncation());
    }
  }

  private updateTextTruncation(): void {
    if (typeof this.value === 'string' && this.type !== 'RATIO') {
      const inputEl = this.formInputElRef?.nativeElement?.querySelector('input');
      if (inputEl) {
        const truncated = inputEl.scrollWidth > inputEl.offsetWidth;
        if (truncated !== this.isValueTruncated) {
          this.isValueTruncated = truncated;
          this._cdr.markForCheck();
        }
      }
    }
  }

  get displayValue(): any {
    /** Comment out this block if you want to always show the formatted value */
    if (this.isEditing) {
      return this.value ?? '';
    }

    if (typeof this.value === InputType.NUMBER) {
      const snPiped = this.snPipe.transform(this.value) ?? '0';
      if (snPiped !== this.value + '') {
        this.isValueTruncated = true;
      }

      return snPiped;
    }

    return this.value ?? '';
  }

  handleValueChange($event: any): void {
    this.isDirty = true;
    this.valueCache = $event;
  }

  formFieldFocused() {
    if (!this.readOnly) {
      this.isEditing = true;
    }
  }

  formFieldBlurred() {
    this.isEditing = false;

    if (this.isDirty) {
      this.submit();
    }
  }

  onKeydownEnter(elementRef: any) {
    elementRef.blurFormField();
  }

  submit() {
    this.isEditing = false;
    this.isDirty = false;

    if (!this.valueCache) {
      return;
    }

    this.value = this.valueCache;
    this.valueChange.emit(this.value);
    this.update.emit(this.value); // legacy support
  }

  /**
   * BELOW is for ratio
   */
  private transformUnitValue(): void {
    if (this.unit) {
      this.unitStr = this.unitService.getChoice(this.unit as UnitType) || '';
    }
  }

  private setRatioValue(value: number): void {
    if (!this.unit) return;

    const unitType = this.unit as UnitType;
    const max = this.unitService.getChoice(unitType) === '%' ? 100 : 1;
    const transformedValue =
      this.snPipe.transform(max - ((value as number) ?? 0)) ?? 0;
    this.ratioValue = +transformedValue;

    this._cdr.detectChanges();
  }

  protected checkInput($event: Event): void {
    const value = ($event.target as HTMLInputElement).value;
    if (this.type === 'RATIO') {
      this.setRatioValue(Number(value));
    }
  }

  protected stopProp($event: Event): void {
    $event.stopPropagation();
  }

  protected setInitial($event: Event): void {
    this.initialValue = ($event.target as HTMLInputElement).value;
  }

  protected updateValue($event: Event | FocusEvent): void {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    const value = ($event.target as HTMLInputElement).value;
    if (value === this.initialValue) return;
    // if (this.updater) {
    //   this.loading = true;
    //   this._cdr.detectChanges();
    // }
    // reset initial value? or just check if it's different?
    this.initialValue = value;
    this.update.emit(value);
  }
}
